<template>
	<div class="p-10">
		<div class="flex justify-between">
			<h1>{{ $t('routerTitles.ReferenceData') }}</h1>
		</div>
		<el-card>
			<div class="filters">
				<el-form inline class="filters-form" @submit.native.prevent>
					<el-form-item :label="$t('referenceData.typeFilter')">
						<el-select
							v-model="selectedType"
							size="mini"
							:placeholder="$t('referenceData.selectType')"
							style="min-width: 220px"
						>
							<el-option
								v-for="t in referenceDataTypes"
								:key="t"
								:label="t"
								:value="t"
							/>
						</el-select>
					</el-form-item>
					<div class="filters-actions">
						<el-button
							type="primary"
							size="mini"
							:disabled="!hasManagePermission"
							@click="handleExportCsv"
						>
							{{ $t('referenceData.export') }}
							<i v-if="!hasManagePermission" class="el-icon-lock"></i>
						</el-button>
						<el-button
							type="primary"
							size="mini"
							:disabled="!hasManagePermission"
							@click="importDialogVisible = true"
						>
							{{ $t('referenceData.import') }}
							<i v-if="!hasManagePermission" class="el-icon-lock"></i>
						</el-button>
					</div>
				</el-form>
			</div>
			<rslt-ag-grid
				grid-key="reference-data-grid"
				grid-class="reference-data-grid"
				:column-defs="columnDefs"
				:row-data="gridRowData"
				:loading="loading"
				:get-row-node-id="getRowNodeId"
				@grid-ready="onGridReady"
			/>
		</el-card>

		<reference-data-import
			:visible.sync="importDialogVisible"
			:participant="selectedParticipantStore"
			:legal-entity="selectedLegalEntityStore"
			:reference-data-type="selectedType"
			@imported="fetchReferenceData"
		/>
	</div>
</template>

<script>
import { mapGetters } from 'vuex';
import gql from 'graphql-tag';
import { hasLicenseAndPermission } from '@/helpers/permission-helper';
import RsltAgGrid from '@/components/RsltAgGrid/index.vue';
import ReferenceDataImport from './components/ReferenceDataImport.vue';

const REFERENCE_DATA_TYPES = gql`
	query ReferenceDataTypes($participant: uuid!) {
		risolto_reference_data(
			where: { participant: { _eq: $participant } }
			distinct_on: reference_data_type
			order_by: { reference_data_type: asc }
		) {
			reference_data_type
		}
	}
`;

const REFERENCE_DATA = gql`
	query ReferenceDataList($participant: uuid!, $legal_entity: uuid!, $referenceDataType: String!) {
		risolto_reference_data(
			where: {
				participant: { _eq: $participant }
				legal_entity: { _eq: $legal_entity }
				reference_data_type: { _eq: $referenceDataType }
			}
			order_by: { key: asc }
		) {
			reference
			key
			reference_data
		}
	}
`;

export default {
	name: 'ReferenceDataIndex',
	components: {
		RsltAgGrid,
		ReferenceDataImport
	},
	data() {
		return {
			referenceDataTypes: [],
			selectedType: null,
			rows: [],
			loading: false,
			gridApi: null,
			importDialogVisible: false
		};
	},
	computed: {
		...mapGetters(['selectedParticipantStore', 'selectedLegalEntityStore']),
		hasManagePermission() {
			const licences = this.$store.state.risolto.licences[this.selectedParticipantStore];
			const permissions = this.$store.state.risolto.permissions[this.selectedParticipantStore];
			return hasLicenseAndPermission(licences, permissions, 'REFERENCE_DATA', 'REFERENCE_DATA_MANAGE');
		},
		gridRowData() {
			return this.rows.map(row => ({
				reference: row.reference,
				...(row.reference_data || {})
			}));
		},
		keyFieldName() {
			// We need to find the key field name based on the key value in the first row
			if (!this.rows.length || !this.rows[0].reference_data) {
				return null;
			}

			const first = this.rows[0];
			const keyVal = first.key;
			const data = first.reference_data;
			const match = Object.keys(data).find(k => data[k] != null && String(data[k]) === String(keyVal));
			return match || null;
		},
		keyTag() {
			return this.$t('referenceData.keyColumnTag');
		},
		columnDefs() {
			if (!this.rows.length || !this.rows[0].reference_data) {
				return [];
			}

			const dataKeys = Object.keys(this.rows[0].reference_data);
			const keyField = this.keyFieldName;
			const tag = this.keyTag;
			return dataKeys.map(field => ({
				field,
				headerName: field,
				// We add a tag to the key field to indicate that it is the key field
				headerValueGetter: keyField === field ? () => `${field} (${tag})` : undefined,
				sortable: true,
				filter: 'agTextColumnFilter',
				minWidth: 120
			}));
		}
	},
	watch: {
		selectedParticipantStore() {
			this.fetchTypes();
			this.selectedType = null;
			this.rows = [];
		},
		selectedType() {
			this.fetchReferenceData();
		}
	},
	created() {
		this.fetchTypes();
	},
	methods: {
		getRowNodeId(data) {
			return data.reference;
		},
		onGridReady(params) {
			this.gridApi = params.api;
		},
		handleExportCsv() {
			this.gridApi.exportDataAsCsv({
				fileName: 'reference-data.csv',
				// We need to process the header names to remove the (key) tag
				processHeaderCallback: (params) => params.column.getColDef().headerName
			});
		},
		async fetchTypes() {
			try {
				const result = await this.$apollo.query({
					query: REFERENCE_DATA_TYPES,
					variables: { participant: this.selectedParticipantStore }
				});

				if (!result.data || !result.data.risolto_reference_data) {
					return;
				}

				this.referenceDataTypes = result.data.risolto_reference_data.map(r => r.reference_data_type).filter(Boolean);
				if (this.referenceDataTypes.length && !this.selectedType) {
					this.selectedType = this.referenceDataTypes[0];
				}
			} catch (e) {
				this.$message.error(this.$t('error.fetchFailed'));
			}
		},
		async fetchReferenceData() {
			this.loading = true;
			try {
				const result = await this.$apollo.query({
					query: REFERENCE_DATA,
					variables: {
						participant: this.selectedParticipantStore,
						legal_entity: this.selectedLegalEntityStore,
						referenceDataType: this.selectedType
					}
				});

				if (!result.data || !result.data.risolto_reference_data) {
					return;
				}

				this.rows = result.data.risolto_reference_data;
			} catch (e) {
				this.$message.error(this.$t('error.fetchFailed'));
			} finally {
				this.loading = false;
			}
		}
	}
};
</script>

<style scoped>
.filters {
	margin-bottom: 16px;
}
.filters-form {
	display: flex;
	align-items: center;
	justify-content: space-between;
	width: 100%;
}
.filters-actions {
	margin-left: auto;
}
.reference-data-grid {
	height: calc(100vh - 280px);
}
</style>
